import React, { useState, useRef, useEffect } from 'react';
import type { KeyframeMarkerProps } from '../types';
import { WithTooltip } from '@/components/ui/tooltip';
import { getAssetCursor } from '@/pages/editor/components/timeline/TimelineCategoryItem/utils/assetStateStyles';
import { LOCKED_FILTER } from '@/pages/editor/components/timeline/utils';
import { seekPlayheadTo } from '@/modules/timelineTransport/seekPlayheadTo';

export const KeyframeMarker: React.FC<KeyframeMarkerProps> = ({
  keyframe,
  assetStartTime,
  assetDuration,
  scale,
  isLocked,
  isVisible,
  onMove,
  onMoveEnd, 

  onSelect,

  isSelected = false,
  assetInstanceId,
  isPlaying = false,
  isLastKeyframe = false
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const markerRef = useRef<HTMLDivElement>(null);
  const originalTimePositionRef = useRef<number | null>(null);
  const didDragRef = useRef(false);

  // Validate keyframe data to prevent crashes - ensure timePosition is a valid number
  const timePos = typeof keyframe?.timePosition === 'string' 
    ? parseFloat(keyframe.timePosition) 
    : keyframe?.timePosition;
  
  // Use the parsed numeric value for safe operations
  const safeKeyframe = keyframe ? { ...keyframe, timePosition: timePos ?? 0 } : null;
  
  // Removed handleRightClick - right-click/two-finger delete gesture should not delete keyframes directly
  // Keyframes can only be deleted via keyboard shortcuts (Backspace/Delete) or context menu delete button
  // Right-clicks on keyframe markers will bubble up to parent TimelineCategoryItem to show context menu

  const isValidKeyframe = !!safeKeyframe && timePos !== undefined && timePos !== null && !isNaN(timePos);
  const keyframeId = safeKeyframe?.id;
  const isStartMarker = safeKeyframe?.category === 'start';
  const isEndMarker = safeKeyframe?.category === 'end';

  useEffect(() => {
  if (!isValidKeyframe || keyframeId === undefined) return; // invalid markers render nothing
  if (!isDragging || isStartMarker) return; // only start not draggable
  const handleMove = (e: MouseEvent | PointerEvent) => {
    // Always allow dragging (removed edit mode check)
    if (isLocked) return;
    didDragRef.current = true; // Mark that we actually dragged
    const assetContainer = markerRef.current?.parentElement?.parentElement;
    if (!assetContainer) return;
    const assetRect = assetContainer.getBoundingClientRect();
    const mouseX = e.clientX - assetRect.left;
    const newRelativeTime = (mouseX / assetRect.width) * assetDuration;
    const clampedRelativeTime = Math.max(0, Math.min(assetDuration, newRelativeTime));
    onMove?.(keyframeId, clampedRelativeTime);
  };
  const handleUp = () => {
    setIsDragging(false);
    setIsHovered(false);
    // Call onMoveEnd when drag ends, passing the original time position
    if (originalTimePositionRef.current !== null) {
      onMoveEnd?.(keyframeId, originalTimePositionRef.current);
      originalTimePositionRef.current = null;
    }
  };
  // Route listeners to the document owning the marker element, so keyframe
  // drag works when the timeline is detached into a popup window. Listen to
  // both mouse and pointer events so touch drags work too.
  const doc = markerRef.current?.ownerDocument ?? document;
  doc.addEventListener('mousemove', handleMove as EventListener);
  doc.addEventListener('mouseup', handleUp);
  doc.addEventListener('pointermove', handleMove as EventListener);
  doc.addEventListener('pointerup', handleUp);
  doc.addEventListener('pointercancel', handleUp);
  return () => {
    doc.removeEventListener('mousemove', handleMove as EventListener);
    doc.removeEventListener('mouseup', handleUp);
    doc.removeEventListener('pointermove', handleMove as EventListener);
    doc.removeEventListener('pointerup', handleUp);
    doc.removeEventListener('pointercancel', handleUp);
  };
}, [isValidKeyframe, isDragging, isLocked, isStartMarker, isEndMarker,
  assetStartTime, assetDuration, onMove, onMoveEnd, keyframeId]);

  // Early return for invalid keyframe data
  if (!safeKeyframe || timePos === undefined || timePos === null || isNaN(timePos)) {
    return null;
  }

  // Absolute -> relative -> pixel position
  const keyframeAbsoluteTime = safeKeyframe.timePosition;
  const assetAbsoluteStartTime = assetStartTime ?? 0;
  const relativeTime = keyframeAbsoluteTime - assetAbsoluteStartTime;
  const safeDuration = assetDuration || 1; // Prevent division by zero
  const xPosition = (relativeTime / safeDuration) * (safeDuration * scale);

  // Exact left coordinate: 0 for start, time-based position for all others (including end keyframes)
  const finalLeft = isStartMarker ? 0 : xPosition;

  // Centred on its time, at 0 too: the lanes draw past 0 at the start of the timeline
  const transform = 'translate(-50%, -50%)';


const handleClick = (e: React.MouseEvent) => {
    // Don't allow selection if asset is locked, hidden, or timeline is playing
    if (isLocked || !isVisible || isPlaying) {
      e.stopPropagation();
      return;
    }
    
    // If we just finished a drag, prevent the click event from selecting
    // The browser fires click after mouseup, but onMoveEnd has already selected it
    if (didDragRef.current) {
      e.stopPropagation();
      e.preventDefault();
      didDragRef.current = false; // Reset for next time
      return;
    }
    
    // Always send selection when clicking (not dragging)
    if (!isDragging) {
      e.stopPropagation(); // Prevent duplicate sends - parent container also has a click handler
      seekPlayheadTo(safeKeyframe.timePosition);
      onSelect?.(safeKeyframe.id);
    }
  };

const handleMouseDown = (e: React.MouseEvent) => {
    // Disable dragging when locked, hidden, playing, or for start marker
    if (isLocked || !isVisible || isStartMarker || isPlaying) return;
    e.stopPropagation();
    e.preventDefault();
    // Capture the original time position when drag starts
    originalTimePositionRef.current = safeKeyframe.timePosition;
    setIsDragging(true);
  };

const handlePointerDown = (e: React.PointerEvent) => {
    // Touch entry point — mouse already handled by onMouseDown above.
    if (e.pointerType !== 'touch') return;
    if (isLocked || !isVisible || isStartMarker || isPlaying) return;
    e.stopPropagation();
    originalTimePositionRef.current = safeKeyframe.timePosition;
    setIsDragging(true);
  };

  // Get cursor from centralized styling system
  const cursor = getAssetCursor(isLocked, isVisible, 'pointer');

  return (
    <div
      ref={markerRef}
      className={`absolute top-1/2 ${
        isDragging
          ? 'cursor-grabbing'
          : '' // Use custom cursor from style
      }`}
      style={{
        left: `${finalLeft}px`,
        transform: transform, // Use conditional transform: no X translation at position 0 to avoid clipping
        pointerEvents: 'auto',
        cursor: cursor,
        // Disable browser touch gestures (scroll/zoom) so pointermove fires
        // through a touch drag instead of being cancelled mid-gesture.
        touchAction: 'none',
        filter: isLocked ? LOCKED_FILTER : 'none'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onPointerDown={handlePointerDown}
      onDragStart={(e) => e.preventDefault()}
      // Removed onContextMenu - let parent TimelineCategoryItem handle right-click to show context menu
      // This prevents two-finger delete gesture from directly deleting keyframes
    >
      <WithTooltip
        content={
          isStartMarker
            ? `Start keyframe at ${safeKeyframe.timePosition.toFixed(2)}s (selectable, not movable)`
            : `Keyframe at ${safeKeyframe.timePosition.toFixed(2)}s`
        }
      >
      {/* The edge in the secondary purple, as production drew its diamonds */}
      <div
        className={`${
          isStartMarker
            ? isSelected
              ? 'bg-lureo-aqua border-lureo-line w-3.5 h-3.5 border-2' // theme-exempt: production's 2px diamond edge, a mark not a line
              : 'bg-lureo-aqua border-lureo-secondary w-2.5 h-2.5 border-2' // theme-exempt: production's 2px diamond edge, a mark not a line
            : isLastKeyframe
              ? isSelected
                ? 'bg-lureo-coral w-3.5 h-3.5'
                : 'bg-lureo-coral w-2.5 h-2.5'
              : isSelected
                ? 'bg-lureo-aqua border-lureo-secondary/90 w-3.5 h-3.5 border-2' // theme-exempt: production's 2px diamond edge, a mark not a line
                : 'bg-lureo-aqua border-lureo-secondary/50 w-2.5 h-2.5 border-2' // theme-exempt: production's 2px diamond edge, a mark not a line
        } ${isHovered ? 'scale-110' : ''} transition-transform duration-lureo-duration-slow `}
        style={{
          transform: 'rotate(45deg)',
          transformOrigin: 'center',
        }}
        data-keyframe-marker
        data-asset-id={assetInstanceId}
      />
      </WithTooltip>
    </div>
  );
}; 